// IKAIKA: dynamic cart recommendations (cart drawer + cart page), plus hooks for the Section Store
// "Pairs well with" card on product pages (refreshes the cart drawer on Add, matches the ring shown
// on the page: same color, and same size when available, and protects its labels from the theme's
// price updater).
//
// Cart recommendations fill the [data-ikaika-recs] shell rendered by snippets/cross-sells.liquid.
// Logic:
//   remaining = free shipping threshold - cart total
//   remaining > 0  -> suggest ONLY items that clear the gap (cheapest first, but always including the
//                     best Shopify "related" match if it clears it), so a $19 stacker is the nudge
//                     when someone is a few dollars short. If nothing clears it, show the closest.
//   remaining <= 0 -> Shopify's related-product picks for the most recently added item.
// Each suggestion pre-selects the same color / size as the item the shopper added last.
(function () {
  'use strict';

  var EXCLUDE = { 'mystery-item': 1, 'gift-card': 1 };
  var MAX_SHOWN = 3;

  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function getJSON(url) {
    return fetch(url, { credentials: 'include' }).then(function (r) {
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    });
  }

  function normRec(p) {
    var img = p.featured_image && (p.featured_image.src || p.featured_image);
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      url: p.url,
      img: typeof img === 'string' ? img : '',
      type: p.type,
      src: 'rec',
      variants: (p.variants || [])
        .filter(function (v) { return v.available; })
        .map(function (v) { return { id: v.id, title: v.title, price: v.price, o1: v.option1, o2: v.option2 }; })
    };
  }

  function same(a, b) {
    return String(a == null ? '' : a).toLowerCase() === String(b == null ? '' : b).toLowerCase();
  }

  function pickVariant(prod, pref) {
    var vs = prod.variants;
    if (!vs.length) return null;
    var hit = null;
    if (pref) {
      hit = vs.filter(function (v) { return same(v.o1, pref[0]) && same(v.o2, pref[1]); })[0] ||
            vs.filter(function (v) { return same(v.o2, pref[1]); })[0] ||
            vs.filter(function (v) { return same(v.o1, pref[0]); })[0];
    }
    return hit || vs[0];
  }

  function byRecFirst(a, b) {
    return (a.src === 'rec' ? 0 : 1) - (b.src === 'rec' ? 0 : 1) || a.order - b.order;
  }

  // Returns { items, clears }: clears is true when every item shown gets the cart to free shipping.
  function choose(cands, remaining, pref) {
    cands.forEach(function (c, i) {
      c.order = i;
      c.pick = pickVariant(c, pref);
      c.price = c.pick ? c.pick.price : 0;
    });
    cands = cands.filter(function (c) { return c.pick; });

    if (remaining <= 0) return { items: cands.sort(byRecFirst).slice(0, MAX_SHOWN), clears: false };

    var yes = cands.filter(function (c) { return c.price >= remaining; })
      .sort(function (a, b) { return a.price - b.price || byRecFirst(a, b); });

    if (yes.length) {
      var out = [yes[0]];
      var bestRec = yes.filter(function (c) { return c.src === 'rec' && out.indexOf(c) === -1; })
        .sort(function (a, b) { return a.order - b.order; })[0];
      if (bestRec) out.push(bestRec);
      yes.forEach(function (c) { if (out.length < MAX_SHOWN && out.indexOf(c) === -1) out.push(c); });
      return { items: out.slice(0, MAX_SHOWN), clears: true };
    }

    var closest = cands.sort(function (a, b) { return b.price - a.price || a.order - b.order; });
    return { items: closest.slice(0, MAX_SHOWN), clears: false };
  }

  function rowHTML(c) {
    var v = c.pick;
    var select;
    if (c.variants.length > 1) {
      select = '<select class="ikaika-rec__select" aria-label="Choose an option for ' + esc(c.title) + '">' +
        c.variants.map(function (x) {
          return '<option value="' + x.id + '" data-price="' + x.price + '"' + (x.id === v.id ? ' selected' : '') + '>' + esc(x.title) + '</option>';
        }).join('') + '</select>';
    } else {
      select = '<input type="hidden" class="ikaika-rec__select" value="' + v.id + '" data-price="' + v.price + '">';
    }
    return '<div class="ikaika-rec">' +
      '<a class="ikaika-rec__img" href="' + esc(c.url) + '"><img src="' + esc(sized(c.img, 160)) + '" alt="" width="64" height="64" loading="lazy"></a>' +
      '<div class="ikaika-rec__body">' +
        '<a class="ikaika-rec__title" href="' + esc(c.url) + '">' + esc(c.title) + '</a>' +
        '<div class="ikaika-rec__meta"><span class="ikaika-rec__price">' + money(c.price) + '</span></div>' +
        '<div class="ikaika-rec__controls">' + select +
        '<button type="button" class="ikaika-rec__add btn btn--tertiary btn--x-small">Add</button></div>' +
      '</div></div>';
  }

  function populate(el) {
    if (el.__busy) return;
    el.__busy = true;

    getJSON('/cart.js').then(function (cart) {
      var items = cart.items || [];
      if (!items.length) { el.hidden = true; return; }

      var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 0;
      var remaining = threshold > 0 ? Math.max(0, threshold - cart.total_price) : 0;
      var inCart = {};
      items.forEach(function (i) { inCart[i.product_id] = 1; });

      var last = items[0];
      var pref = last.variant_options && last.variant_options.length ? last.variant_options.slice(0, 2) : null;

      var pool = [];
      try { pool = JSON.parse(el.querySelector('[data-ikaika-pool]').textContent) || []; } catch (e) {}
      pool.forEach(function (p) { p.src = 'pool'; });

      return getJSON('/recommendations/products.json?product_id=' + last.product_id + '&limit=10&intent=related')
        .then(function (r) { return (r.products || []).map(normRec); }, function () { return []; })
        .then(function (recs) {
          var seen = {};
          var cands = recs.concat(pool).filter(function (p) {
            if (!p || inCart[p.id] || seen[p.handle] || EXCLUDE[p.handle] || p.type === 'Gift Card') return false;
            seen[p.handle] = 1;
            return p.variants.length > 0;
          });

          var result = choose(cands, remaining, pref);
          if (!result.items.length) { el.hidden = true; return; }

          var heading = el.querySelector('[data-ikaika-recs-heading]');
          if (heading) {
            heading.textContent = result.clears
              ? heading.getAttribute('data-short-of-shipping')
              : heading.getAttribute('data-default');
          }

          el.setAttribute('data-remaining', String(remaining));
          el.querySelector('[data-ikaika-recs-list]').innerHTML = result.items.map(rowHTML).join('');
          el.hidden = false;
        });
    }).catch(function () {
      el.hidden = true;
    }).then(function () {
      el.__busy = false;
      el.setAttribute('data-ikaika-ready', '1');
    });
  }

  function fallbackAdd(vid, btn) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1 }] })
    }).then(function () { window.location.reload(); }, function () { btn.disabled = false; btn.textContent = 'Add'; });
  }

  function add(el, row, btn) {
    var vid = row.querySelector('.ikaika-rec__select').value;
    var proxy = el.querySelector('[data-add-item-id]');
    btn.disabled = true;
    btn.textContent = 'Adding';
    if (!proxy) { fallbackAdd(vid, btn); return; }

    // Route through the theme's own cross-sell button so the drawer / cart page refresh natively.
    proxy.setAttribute('data-add-item-id', vid);
    proxy.setAttribute('data-quantity-minimum', '1');
    proxy.click();

    // Safety net: if the theme handler wasn't bound for any reason, add directly and reload.
    setTimeout(function () {
      getJSON('/cart.js').then(function (cart) {
        var has = (cart.items || []).some(function (i) { return String(i.variant_id) === String(vid); });
        if (!has) fallbackAdd(vid, btn);
      });
    }, 2500);
  }

  function bind(el) {
    if (el.__bound) return;
    el.__bound = true;

    el.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.ikaika-rec__add');
      if (!btn) return;
      e.preventDefault();
      add(el, btn.closest('.ikaika-rec'), btn);
    });

    el.addEventListener('change', function (e) {
      var sel = e.target;
      if (!sel || sel.tagName !== 'SELECT' || !sel.classList.contains('ikaika-rec__select')) return;
      var row = sel.closest('.ikaika-rec');
      var price = parseInt(sel.options[sel.selectedIndex].getAttribute('data-price'), 10);
      row.querySelector('.ikaika-rec__price').textContent = money(price);
    });
  }

  function scan() {
    document.querySelectorAll('[data-ikaika-recs]').forEach(function (el) {
      bind(el);
      if (!el.hasAttribute('data-ikaika-ready')) populate(el);
    });
  }

  function refresh() {
    document.querySelectorAll('[data-ikaika-recs]').forEach(function (el) { el.removeAttribute('data-ikaika-ready'); });
    scan();
  }

  // ---------------------------------------------------------------------------------------------
  // Section Store "Pairs well with" card (product pages).
  //
  // 1) Add button: it adds to the cart but this theme's drawer doesn't hear about it, so the drawer
  //    would show stale contents. When the drawer is available, add through the theme's own hidden
  //    cross-sell button instead (which refreshes the drawer), then open the drawer. If the cart is
  //    empty there is no drawer button yet, so the Section Store handler runs as normal (it sends the
  //    shopper to the cart page).
  //
  // 2) Match what's on the page: when the shopper picks a color (or size) on the main product, each
  //    paired ring switches to the same color, and the same size when that size is in stock.
  //
  // 3) Label guard: when the shopper changes the main product's variant, the theme's price updater
  //    overwrites the text of EVERY element marked data-price inside the product area, which includes
  //    the card's size options and Add buttons (they all turned into "$59"). Restore them right away.
  // ---------------------------------------------------------------------------------------------
  function drawerProxy() {
    return document.querySelector('.quick-cart [data-add-item-id]');
  }

  function openDrawer() {
    var trigger = document.querySelector('a[href="/cart"][aria-label], a[href="/cart"], [data-cart-trigger]');
    if (trigger) trigger.click();
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-mini-add-to-cart]');
    if (!btn || btn.disabled) return;
    var vid = btn.dataset.variantId;
    var proxy = drawerProxy();
    if (!vid || !proxy) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var card = btn.closest('[data-mini-atc]');
    var select = card && card.querySelector('[data-mini-atc-variant-select]');
    btn.disabled = true;
    btn.textContent = btn.dataset.addingLabel || 'Adding...';

    proxy.setAttribute('data-add-item-id', vid);
    proxy.setAttribute('data-quantity-minimum', '1');
    proxy.click();

    var tries = 0;
    (function waitForCart() {
      getJSON('/cart.js').then(function (cart) {
        var has = (cart.items || []).some(function (i) { return String(i.variant_id) === String(vid); });
        if (has) {
          btn.textContent = btn.dataset.addedLabel || 'Added';
          setTimeout(openDrawer, 350);
          setTimeout(function () {
            btn.disabled = false;
            if (select) select.dispatchEvent(new Event('change'));
          }, 2500);
        } else if (++tries < 10) {
          setTimeout(waitForCart, 400);
        } else {
          btn.disabled = false;
          if (select) select.dispatchEvent(new Event('change'));
        }
      });
    })();
  }, true);

  function pageSelection() {
    var sels = document.querySelectorAll('.product__variants-wrapper select[name^="options["]');
    if (!sels.length) return null;
    return Array.prototype.map.call(sels, function (s) { return s.value; });
  }

  function optionParts(opt) {
    // "5 / Gold - Sold Out" -> ["5", "Gold"]
    var txt = opt.dataset.ikOrig != null ? opt.dataset.ikOrig : opt.textContent;
    return txt.split(/\s[–—-]\s/)[0].split(' / ').map(function (s) { return s.trim(); });
  }

  function matchCards(sel) {
    document.querySelectorAll('[data-ss-upsell-root] select[data-mini-atc-variant-select]').forEach(function (select) {
      var opts = Array.prototype.slice.call(select.options);
      var wantColor = sel[sel.length - 1];
      var exact = null, colorOnly = null;
      opts.forEach(function (o) {
        if (o.disabled) return;
        var parts = optionParts(o);
        var colorOk = same(parts[parts.length - 1], wantColor);
        if (colorOk && parts.length === sel.length && parts.every(function (p, i) { return same(p, sel[i]); })) exact = exact || o;
        if (colorOk) colorOnly = colorOnly || o;
      });
      var target = exact || (sel.length > 1 ? colorOnly : null);
      if (target && select.value !== target.value) {
        select.value = target.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function guardLabels(root) {
    root.querySelectorAll('option').forEach(function (o) {
      if (o.dataset.ikOrig == null) o.dataset.ikOrig = o.textContent;
    });

    var busy = false, t;
    function fix() {
      busy = true;
      root.querySelectorAll('option').forEach(function (o) {
        if (o.dataset.ikOrig != null && o.textContent !== o.dataset.ikOrig) o.textContent = o.dataset.ikOrig;
      });
      root.querySelectorAll('[data-mini-atc]').forEach(function (card) {
        var b = card.querySelector('[data-mini-add-to-cart]');
        var s = card.querySelector('[data-mini-atc-variant-select]');
        if (!b || !s || b.dataset.loading === 'true') return;
        var txt = b.textContent.trim();
        var label = b.dataset.atcLabel || 'Add';
        var known = [label, b.dataset.soldOutLabel, b.dataset.addingLabel, b.dataset.addedLabel].filter(Boolean);
        var ok = known.some(function (k) { return txt.indexOf(k) === 0; });
        if (!ok) s.dispatchEvent(new Event('change'));
      });
      busy = false;
    }
    new MutationObserver(function () {
      if (busy) return;
      clearTimeout(t);
      t = setTimeout(fix, 20);
    }).observe(root, { subtree: true, childList: true, characterData: true });
  }

  function watchPageSelection() {
    var last = null;
    var tries = 0;
    var starter = setInterval(function () {
      tries++;
      var root = document.querySelector('[data-ss-upsell-root]');
      if (root && pageSelection()) {
        clearInterval(starter);
        guardLabels(root);
        setInterval(function () {
          var sel = pageSelection();
          if (!sel) return;
          var sig = sel.join('|');
          if (sig !== last) { last = sig; matchCards(sel); }
        }, 400);
      } else if (tries > 40) {
        clearInterval(starter);
      }
    }, 250);
  }

  // Re-run after any cart mutation (the theme re-renders the drawer / cart page after these).
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = nativeFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
        p.then(function () { setTimeout(refresh, 700); }, function () {});
      }
      return p;
    };
  }

  var timer;
  new MutationObserver(function () {
    clearTimeout(timer);
    timer = setTimeout(scan, 120);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  watchPageSelection();
})();